import { inject, Injectable, signal } from '@angular/core';
import { ColumnDef } from '@tanstack/angular-table';
import { ToastrService } from 'ngx-toastr';
import { ModalService } from '@core/modals/services/modal.service';
import { SocketService } from '@core/services/socket.service';
import { SessionService } from '@core/services/session.service';
import {
  coinflipHistoryTableConfig,
  jackpotHistoryTableConfig,
  kingHistoryTableConfig,
  minesHistoryTableConfig,
  towersHistoryTableConfig,
  upgraderHistoryTableConfig,
  wheelHistoryTableConfig,
  plinkoHistoryTableConfig,
  casesHistoryConfig,
  caseBattlesHistoryConfig,
} from '../configs';
import {
  CoinflipHistory,
  JackpotHistory,
  MinesHistory,
  TowersHistory,
  UpgraderHistory,
  WheelHistory,
  PlinkoHistory,
  CasesHistory,
  CaseBattlesHistory,
  KingUserHistory,
} from '../interfaces';
import { depositsHistoryTableConfig } from '../configs/deposits-history.config';
import { DepositsHistory } from '../interfaces/deposits-history.interface';
import { TradesHistory } from '../interfaces/trades-history.interface';
import { tradesHistoryTableConfig } from '../configs/trades-history.config';
import { WithdrawalsHistory } from '../interfaces/withdrawals-history.interface';
import { withdrawalsHistoryTableConfig } from '../configs/withdrawls-history.config';
import { ClipboardService } from '@core/services/clipboard.service';
import { Router } from '@angular/router';
import { HistorySkins } from '../types/history-shared.type';
import { Skin } from '@core/interfaces/skin.interface';
import { assignColorToSkin } from '@core/utils/skins.util';
import { CasesService } from '@features/games/cases/services/cases.service';

@Injectable({
  providedIn: 'root',
})
export class UserHistoryService {
  private modalService = inject(ModalService);
  private socketService = inject(SocketService);
  private session = inject(SessionService);
  private clipboardService = inject(ClipboardService);
  private toastrService = inject(ToastrService);
  private router = inject(Router);
  private casesService = inject(CasesService);
  readonly minesColumns = signal<ColumnDef<MinesHistory>[]>(
    minesHistoryTableConfig({
      copy: this.clipboardService.copy.bind(this.clipboardService),
      modalService: this.modalService,
    })
  );

  readonly towersColumns = signal<ColumnDef<TowersHistory>[]>(
    towersHistoryTableConfig({
      copy: this.clipboardService.copy.bind(this.clipboardService),
      modalService: this.modalService,
    })
  );

  readonly jackpotsColumns = signal<ColumnDef<JackpotHistory>[]>(
    jackpotHistoryTableConfig({
      modalService: this.modalService,
      userHistoryService: this,
    })
  );

  readonly coinflipsColumns = signal<ColumnDef<CoinflipHistory>[]>(
    coinflipHistoryTableConfig({
      modalService: this.modalService,
      userHistoryService: this,
    })
  );

  readonly wheelColumns = signal<ColumnDef<WheelHistory>[]>(
    wheelHistoryTableConfig({
      modalService: this.modalService,
    })
  );

  readonly upgradeColumns = signal<ColumnDef<UpgraderHistory>[]>(
    upgraderHistoryTableConfig({
      modalService: this.modalService,
    })
  );

  readonly plinkoColumns = signal<ColumnDef<PlinkoHistory>[]>(
    plinkoHistoryTableConfig({
      modalService: this.modalService,
    })
  );

  readonly kingColumns = signal<ColumnDef<KingUserHistory>[]>(
    kingHistoryTableConfig({
      user: this.session.profile() || undefined,
      modalService: this.modalService,
      userHistoryService: this,
    })
  );

  readonly casesColumns = signal<ColumnDef<CasesHistory>[]>(
    casesHistoryConfig({
      modalService: this.modalService,
      casesService: this.casesService,
    })
  );

  readonly caseBattlesColumns = signal<ColumnDef<CaseBattlesHistory>[]>(
    caseBattlesHistoryConfig({
      handleBattleNavigate: (battleId) => {
        this.router.navigate(['/battles', battleId], {
          state: {
            isResetActiveBattle: true,
          },
        });
      },
      modalService: this.modalService,
      user: this.session.profile() || undefined,
    })
  );

  readonly depositsColumns = signal<ColumnDef<DepositsHistory>[]>(
    depositsHistoryTableConfig({
      copy: this.clipboardService.copy.bind(this.clipboardService),
    })
  );

  readonly tradesColumns = signal<ColumnDef<TradesHistory>[]>(
    tradesHistoryTableConfig()
  );

  readonly withdrawalsColumns = signal<ColumnDef<WithdrawalsHistory>[]>(
    withdrawalsHistoryTableConfig({
      copy: this.clipboardService.copy.bind(this.clipboardService),
      userHistoryService: this,
    })
  );

  public async resendTrade(
    gameid: number,
    potid: number = -1,
    type: 'jackpot' | 'coinflip' | 'king' | 'withdraws'
  ) {
    if (!this.session.stats().trade_url) {
      // TODO: check how it works in real data
      this.modalService.openModal('UserTradeUrlModal', {
        onMountFocusTradeURL: true,
      });
      this.toastrService.error(
        'You cannot resend trades until you set your trade URL.'
      );
      return;
    }

    const message = await this.socketService.emit('resend_offer', {
      steamid: this.session.profile()?.steamid,
      gameid,
      type: type === 'withdraws' ? 'store' : type,
      // ...(potid > -1 ? { potid } : {}) //todo: check potid maybe need refactoring,
      potid,
    });

    this.toastrService.success(message);
  }

  public getSkinsByHistorySkins(skins?: HistorySkins): Skin[] {
    const isArray = Array.isArray(skins);
    const isValidString = typeof skins === 'string' && skins?.length > 2;

    if (isArray) {
      return skins.map((skin) => assignColorToSkin(skin));
    }

    if (isValidString) {
      const parsedSkins = JSON.parse(skins as unknown as string) as Skin[];
      return parsedSkins.map((skin) => assignColorToSkin(skin));
    }

    return [];
  }
}
